import { Modal, ModalHeader, ModalBody, ModalFooter, Badge, Button, Row, Col, Spinner } from 'reactstrap'
import { formatReadableDate } from '../../../utils/formatDate'
import { formatBDT } from '../../../utils/formatCurrency'

// ─── Config ──────────────────────────────────────────────────────────────────

const ACTION_CONFIG = {
  create:  { color: 'success', label: 'Created',  icon: 'mdi-plus-circle',   verb: 'created'  },
  update:  { color: 'info',    label: 'Updated',  icon: 'mdi-pencil-circle', verb: 'updated'  },
  delete:  { color: 'danger',  label: 'Deleted',  icon: 'mdi-delete-circle', verb: 'deleted'  },
  restore: { color: 'warning', label: 'Restored', icon: 'mdi-restore',       verb: 'restored' },
}

const ACTION_COLORS = {
  success: '#198754', info: '#0d6efd', danger: '#dc3545', warning: '#fd7e14', secondary: '#6c757d',
}

const ACTION_BG = {
  success: '#d1e7dd', info: '#cfe2ff', danger: '#f8d7da', warning: '#fff3cd', secondary: '#e2e3e5',
}

const MODEL_LABELS = {
  'App\\Models\\Account':                'Account',
  'App\\Models\\AccountExpense':         'Expense',
  'App\\Models\\AccountLoan':            'Loan',
  'App\\Models\\AccountVendorBill':      'Vendor Bill',
  'App\\Models\\AccountCustomerPayment': 'Customer Payment',
  'App\\Models\\AccountFundTransfer':    'Fund Transfer',
  'App\\Models\\AccountImporterPayment': 'Importer Payment',
  'App\\Models\\AccountTransaction':     'Transaction',
  'App\\Models\\AccountVendorPayment':   'Vendor Payment',
}

const FIELD_LABELS = {
  account_id: 'Account',          from_account_id: 'From Account',  to_account_id: 'To Account',
  user_id: 'User',                created_by: 'Created By',         updated_by: 'Updated By',
  approved_by: 'Approved By',     received_by: 'Received By',       borrower_id: 'Borrower',
  loan_status_id: 'Loan Status',  bill_status_id: 'Bill Status',    transaction_type_id: 'Transaction Type',
  expense_type_id: 'Expense Type',account_type_id: 'Account Type',  vendor_id: 'Vendor',
  customer_id: 'Customer',        vehicle_id: 'Vehicle',            payment_method_id: 'Payment Method',
  vehicle_importer_id: 'Importer',vehicle_purchase_id: 'Purchase',  reference_type: 'Reference Type',
  reference_id: 'Reference',      amount: 'Amount',                 paid_amount: 'Paid Amount',
  due_amount: 'Due Amount',       total_amount: 'Total Amount',     running_balance: 'Running Balance',
  opening_balance: 'Opening Balance', cached_balance: 'Current Balance',
  loan_date: 'Loan Date',         due_date: 'Due Date',             payment_date: 'Payment Date',
  expense_date: 'Expense Date',   deposit_date: 'Deposit Date',     transaction_date: 'Transaction Date',
  date: 'Date',                   notes: 'Notes',                   purpose: 'Purpose',
  description: 'Description',     narration: 'Narration',           bill_no: 'Bill Number',
  stock_id: 'Stock ID',           status: 'Status',                 is_active: 'Active',
  is_approved: 'Approved',        currency: 'Currency',             name: 'Name',
  title: 'Title',                 username: 'Username',             email: 'Email',
  phone: 'Phone',                 address: 'Address',               type: 'Type',
  category: 'Category',           id: 'ID',
}

const AMOUNT_FIELDS = new Set([
  'amount', 'paid_amount', 'due_amount', 'total_amount',
  'running_balance', 'opening_balance', 'cached_balance',
])

const META_FIELDS = new Set(['id', 'created_at', 'updated_at', 'deleted_at'])

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getFieldLabel = (key) =>
  FIELD_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

const isDateField = (key) =>
  key.endsWith('_at') || key.endsWith('_date') || key.endsWith('_on') || key === 'date'

const formatSmartValue = (key, value) => {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (AMOUNT_FIELDS.has(key)) return formatBDT(value)
  if (isDateField(key)) {
    const f = formatReadableDate(value)
    return f === '-' || f === 'Invalid Date' ? String(value) : f
  }
  if (typeof value === 'object') {
    try { return JSON.stringify(value, null, 2) } catch { return String(value) }
  }
  return String(value)
}

const isEmpty = (v) => v === null || v === undefined || v === ''

const prepareValues = (values) =>
  values
    ? Object.entries(values).filter(([key]) => !META_FIELDS.has(key))
    : []

/** Parse a raw user-agent string into "Browser Version · OS" */
const parseUserAgent = (ua) => {
  if (!ua) return null
  let browser = 'Unknown Browser'
  let os = ''

  if (ua.includes('CrOS')) os = 'Chrome OS'
  else if (ua.includes('Windows NT 10.0') || ua.includes('Windows NT 6.3')) os = 'Windows 10/11'
  else if (ua.includes('Windows NT')) os = 'Windows'
  else if (ua.includes('Macintosh') || ua.includes('Mac OS X')) os = 'macOS'
  else if (ua.includes('Android')) os = 'Android'
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS'
  else if (ua.includes('Linux')) os = 'Linux'

  const opera   = ua.match(/OPR\/(\d+)/)
  const edge    = ua.match(/Edg(?:e)?\/(\d+)/)
  const chrome  = ua.match(/Chrome\/(\d+)/)
  const firefox = ua.match(/Firefox\/(\d+)/)
  const safari  = ua.match(/Version\/[\d.]+ Safari/)

  if (opera)        browser = `Opera ${opera[1]}`
  else if (edge)    browser = `Edge ${edge[1]}`
  else if (chrome)  browser = `Chrome ${chrome[1]}`
  else if (firefox) browser = `Firefox ${firefox[1]}`
  else if (safari)  browser = 'Safari'

  return os ? `${browser} · ${os}` : browser
}

/** Extract a human-readable name from values for the modal title */
const getEntityName = (values) => values?.name || values?.title || null

// ─── Component ───────────────────────────────────────────────────────────────

const AuditLogView = ({ isOpen, toggle, auditLog, isLoading }) => {
  if (!auditLog && !isLoading) return null

  const cfg         = auditLog
    ? (ACTION_CONFIG[auditLog.action] || { color: 'secondary', label: auditLog.action, icon: 'mdi-help-circle', verb: auditLog.action })
    : {}
  const accentColor = ACTION_COLORS[cfg.color] ?? ACTION_COLORS.secondary
  const accentBg    = ACTION_BG[cfg.color]     ?? ACTION_BG.secondary
  const entityLabel = auditLog
    ? (MODEL_LABELS[auditLog.model_type] || auditLog.model_type?.split('\\').pop() || 'Record')
    : ''

  const userName = auditLog?.user?.name || auditLog?.user?.username
  const isSystem = !userName

  const oldDisplay = auditLog?.old_display || auditLog?.old_values
  const newDisplay = auditLog?.new_display || auditLog?.new_values
  const entityName = getEntityName(newDisplay ?? oldDisplay)

  const updateFields  = auditLog?.action === 'update' && auditLog?.old_values && auditLog?.new_values
    ? Object.entries(auditLog.old_values) : []
  const createValues  = auditLog && ['create', 'restore'].includes(auditLog.action) ? prepareValues(newDisplay) : []
  const deleteValues  = auditLog?.action === 'delete' ? prepareValues(oldDisplay) : []
  const parsedUA      = auditLog ? parseUserAgent(auditLog.user_agent) : null

  const modalTitle = !auditLog
    ? 'Loading…'
    : entityName
      ? `${entityLabel} — ${entityName}`
      : `${entityLabel} #${auditLog.model_id}`

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered scrollable>

      {/* ── Header ───────────────────────────────────────────────── */}
      <ModalHeader toggle={toggle} className="border-bottom py-3">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <i className={`mdi ${cfg.icon ?? 'mdi-history'} fs-5`} style={{ color: accentColor }} />
          <span className="fw-semibold">{modalTitle}</span>
          {cfg.label && (
            <span
              className="badge rounded-pill ms-1"
              style={{ background: accentBg, color: accentColor, border: `1px solid ${accentColor}40`, fontSize: 12 }}
            >
              {cfg.label}
            </span>
          )}
        </div>
      </ModalHeader>

      <ModalBody className="p-0">

        {/* ── Loading ──────────────────────────────────────────────── */}
        {isLoading && (
          <div className="text-center py-5">
            <Spinner color="primary" size="sm" />
            <p className="text-muted small mt-2 mb-0">Loading details…</p>
          </div>
        )}

        {!isLoading && auditLog && (<>

          {/* ── Activity Summary ─────────────────────────────────── */}
          <div className="p-4 border-bottom" style={{ background: `${accentBg}90` }}>

            {/* Action sentence */}
            <div className="d-flex align-items-center gap-3 mb-4">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                style={{ width: 44, height: 44, background: accentColor + '1a', border: `2px solid ${accentColor}` }}
              >
                <i className={`mdi ${cfg.icon} fs-5`} style={{ color: accentColor }} />
              </div>
              <div>
                <div className="fw-semibold text-dark" style={{ fontSize: 15 }}>
                  {entityLabel} was <span style={{ color: accentColor }}>{cfg.verb}</span>
                </div>
                {entityName && (
                  <div className="text-muted small mt-1">
                    <i className="mdi mdi-link-variant me-1" />
                    {entityName}
                  </div>
                )}
              </div>
            </div>

            {/* Who / When / Session */}
            <Row className="g-3">
              <Col xs={12} sm={4}>
                <InfoBlock
                  icon="mdi-account-circle-outline"
                  label="Performed by"
                  primary={isSystem ? 'System / Automated' : userName}
                  secondary={!isSystem && auditLog.user?.email ? auditLog.user.email : null}
                  italic={isSystem}
                />
              </Col>
              <Col xs={12} sm={4}>
                <InfoBlock
                  icon="mdi-calendar-clock-outline"
                  label="Date & Time"
                  primary={formatReadableDate(auditLog.created_at)}
                />
              </Col>
              <Col xs={12} sm={4}>
                <InfoBlock
                  icon="mdi-monitor-screenshot"
                  label="Source"
                  primary={auditLog.ip_address || 'Unknown IP'}
                  secondary={parsedUA}
                />
              </Col>
            </Row>
          </div>

          {/* ── Update: What Changed ─────────────────────────────── */}
          {auditLog.action === 'update' && updateFields.length > 0 && (
            <div className="p-4 border-bottom">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="fw-semibold mb-0" style={{ color: accentColor }}>
                  <i className="mdi mdi-swap-horizontal me-2" />
                  What Changed
                </h6>
                <span className="badge bg-light text-muted border" style={{ fontSize: 11 }}>
                  {updateFields.length} field{updateFields.length !== 1 ? 's' : ''} modified
                </span>
              </div>

              {/* Table header */}
              <div className="rounded overflow-hidden border">
                <div className="d-flex bg-light border-bottom px-3 py-2">
                  <div className="small fw-semibold text-uppercase text-muted" style={{ flex: '0 0 28%' }}>Field</div>
                  <div className="small fw-semibold text-uppercase text-danger"   style={{ flex: '0 0 36%' }}>Before</div>
                  <div className="small fw-semibold text-uppercase text-success"  style={{ flex: '0 0 36%' }}>After</div>
                </div>
                {updateFields.map(([key], i) => (
                  <div
                    key={key}
                    className={`d-flex align-items-baseline px-3 py-2 ${i < updateFields.length - 1 ? 'border-bottom' : ''} ${i % 2 !== 0 ? 'bg-light' : ''}`}
                  >
                    <div className="small fw-semibold text-dark" style={{ flex: '0 0 28%' }}>
                      {getFieldLabel(key)}
                    </div>
                    <div style={{ flex: '0 0 36%', paddingRight: 8 }}>
                      <DiffChip value={formatSmartValue(key, oldDisplay?.[key])} variant="danger" />
                    </div>
                    <div style={{ flex: '0 0 36%' }}>
                      <DiffChip value={formatSmartValue(key, newDisplay?.[key])} variant="success" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Create / Restore: Record Details ─────────────────── */}
          {['create', 'restore'].includes(auditLog.action) && createValues.length > 0 && (
            <div className="p-4 border-bottom">
              <h6 className="fw-semibold mb-3" style={{ color: accentColor }}>
                <i className={`mdi ${auditLog.action === 'restore' ? 'mdi-restore' : 'mdi-file-document-outline'} me-2`} />
                {auditLog.action === 'restore' ? 'Restored Record' : 'Record Details'}
              </h6>
              <FieldGrid rows={createValues} formatFn={formatSmartValue} />
            </div>
          )}

          {/* ── Delete: Deleted Record ────────────────────────────── */}
          {auditLog.action === 'delete' && deleteValues.length > 0 && (
            <div className="p-4 border-bottom">
              <h6 className="fw-semibold mb-3" style={{ color: accentColor }}>
                <i className="mdi mdi-delete-outline me-2" />
                Deleted Record
              </h6>
              <FieldGrid rows={deleteValues} formatFn={formatSmartValue} strikethrough />
            </div>
          )}

          {/* ── Log Reference footer ─────────────────────────────── */}
          <div className="px-4 py-2 bg-light border-top d-flex flex-wrap align-items-center gap-3" style={{ fontSize: 12 }}>
            <span className="text-muted">
              <i className="mdi mdi-shield-check-outline me-1" />
              Audit Log <strong className="text-dark">#{auditLog.id}</strong>
            </span>
            <span className="text-muted">·</span>
            <span className="text-muted">
              <i className="mdi mdi-cube-outline me-1" />
              <strong className="text-dark">{entityLabel}</strong>
              {entityName && <span className="ms-1">— {entityName}</span>}
            </span>
            <span className="text-muted">·</span>
            <span className="text-muted">
              <i className="mdi mdi-account-outline me-1" />
              {isSystem
                ? <span className="text-dark fw-semibold">System / Automated</span>
                : <strong className="text-dark">{userName}</strong>
              }
            </span>
          </div>

        </>)}
      </ModalBody>

      <ModalFooter className="border-top">
        <Button color="secondary" size="sm" outline onClick={toggle}>Close</Button>
      </ModalFooter>
    </Modal>
  )
}

// ─── Sub-components ──────────────────────────────────────────────────────────

/** Icon + label + primary text + optional secondary text block */
const InfoBlock = ({ icon, label, primary, secondary = null, italic = false }) => (
  <div className="d-flex align-items-start gap-2">
    <i className={`mdi ${icon} text-muted mt-1`} style={{ fontSize: 18, flexShrink: 0 }} />
    <div style={{ minWidth: 0 }}>
      <div className="small text-muted mb-1">{label}</div>
      <div className={`small fw-semibold text-dark ${italic ? 'fst-italic' : ''}`} style={{ wordBreak: 'break-all' }}>
        {primary}
      </div>
      {secondary && (
        <div className="small text-muted mt-1" style={{ wordBreak: 'break-all' }}>{secondary}</div>
      )}
    </div>
  </div>
)

/** Coloured chip for before/after values in update diffs */
const DiffChip = ({ value, variant }) => {
  if (value === '—') return <span className="small text-muted fst-italic">—</span>

  const styles = {
    danger:  { bg: '#fef2f2', color: '#dc3545', border: '#fca5a5' },
    success: { bg: '#f0fdf4', color: '#16a34a', border: '#86efac' },
  }
  const s = styles[variant]

  return (
    <span
      className="small d-inline-block rounded px-2 py-1"
      style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, wordBreak: 'break-word', lineHeight: 1.5 }}
    >
      {value}
    </span>
  )
}

/** Two-column label/value grid used for create and delete details */
const FieldGrid = ({ rows, formatFn, strikethrough = false }) => (
  <div className="rounded overflow-hidden border">
    {rows.map(([key, val], i) => (
      <div
        key={key}
        className={`d-flex align-items-baseline px-3 py-2 ${i < rows.length - 1 ? 'border-bottom' : ''} ${i % 2 !== 0 ? 'bg-light' : ''}`}
      >
        <div className="small fw-semibold text-muted" style={{ minWidth: 148, flexShrink: 0 }}>
          {getFieldLabel(key)}
        </div>
        <div className={`small ${strikethrough ? 'text-decoration-line-through text-muted' : 'text-dark'}`} style={{ wordBreak: 'break-word' }}>
          {formatFn(key, val)}
        </div>
      </div>
    ))}
  </div>
)

export default AuditLogView
